import { useState, useEffect } from 'react';
import { Bot } from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import CollapsibleSection from './CollapsibleSection';
import TagInput from './TagInput';
import { ConfigItemList } from './ConfigItemList';
import { FrontmatterEditorFields } from './FrontmatterEditorFields';
import { useFrontmatterEditor } from './useFrontmatterEditor';
import { docsApi } from '@/services/docsApi';
import { useWorkspace } from '@/contexts/WorkspaceContext';

interface AgentMeta {
  path: string;
  name: string;
}

export default function AgentsSection() {
  const { selectedRepo } = useWorkspace();
  const [agents, setAgents] = useState<AgentMeta[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const { attrs, body, updateAttr, updateBody, resetContent, clear, config } =
    useFrontmatterEditor(selectedAgent || '__none__');

  useEffect(() => {
    if (!selectedRepo) {
      setLoading(false);
      return;
    }
    const [owner, repo] = selectedRepo.split('/');
    docsApi
      .getRepoTree(owner, repo)
      .then((tree) => {
        const found = findAgents(tree);
        setAgents(found);
      })
      .catch(() => setAgents([]))
      .finally(() => setLoading(false));
  }, [selectedRepo]);

  function findAgents(node: { name: string; path: string; type: string; children: typeof node[] }): AgentMeta[] {
    const results: AgentMeta[] = [];
    if (node.name === 'AGENT.md' && node.path.includes('.claude/agents/')) {
      const parts = node.path.split('/');
      const nameIdx = parts.indexOf('agents') + 1;
      results.push({ path: node.path, name: parts[nameIdx] || node.path });
    }
    for (const child of node.children || []) {
      results.push(...findAgents(child));
    }
    return results;
  }

  function handleCreate(name: string) {
    const safeName = name.toLowerCase().replace(/[^a-z0-9-]/g, '-');
    const path = `.claude/agents/${safeName}/AGENT.md`;
    setAgents([...agents, { path, name: safeName }]);
    setSelectedAgent(path);
    resetContent({ name: safeName, description: '', model: 'sonnet' }, '\nYour agent instructions here.\n');
  }

  function handleDelete() {
    if (!selectedAgent) return;
    setAgents(agents.filter((a) => a.path !== selectedAgent));
    setSelectedAgent(null);
    clear();
  }

  return (
    <CollapsibleSection
      title="Sub-agents"
      description="Custom AI agents with specialized tools and instructions"
      filePath=".claude/agents/"
      icon={Bot}
      exists={agents.length > 0}
      loading={loading}
    >
      {loading ? (
        <LoadingState size="md" />
      ) : (
        <div className="flex gap-4">
          <ConfigItemList
            items={agents}
            selectedPath={selectedAgent}
            onSelect={setSelectedAgent}
            onCreate={handleCreate}
            placeholder="agent-name"
            newLabel="New agent"
          />

          <FrontmatterEditorFields
            attrs={attrs}
            body={body}
            updateAttr={updateAttr}
            updateBody={updateBody}
            loading={config.loading}
            saving={config.saving}
            saved={config.saved}
            onSave={() => config.save(`chore: update agent ${attrs.name || selectedAgent} (via SystemDox)`)}
            onDelete={handleDelete}
            isSelected={!!selectedAgent}
            hasItems={agents.length > 0}
            emptyMessage="Create your first sub-agent"
            extraFields={
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Max turns</label>
                    <input
                      type="number"
                      value={(attrs.maxTurns as number) || ''}
                      onChange={(e) => updateAttr('maxTurns', parseInt(e.target.value) || undefined)}
                      placeholder="20"
                      className="w-full px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
                    />
                  </div>
                  <div className="flex items-end gap-2 pb-1">
                    <label className="flex items-center gap-1.5 text-xs text-slate-500">
                      <input
                        type="checkbox"
                        checked={attrs.isolation === 'worktree'}
                        onChange={(e) => updateAttr('isolation', e.target.checked ? 'worktree' : undefined)}
                        className="rounded"
                      />
                      Worktree isolation
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Allowed tools</label>
                  <TagInput
                    tags={((attrs.tools as string) || '').split(',').map((s) => s.trim()).filter(Boolean)}
                    onChange={(tags) => updateAttr('tools', tags.join(', '))}
                    placeholder="Read, Grep, Glob, Bash"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Disallowed tools</label>
                  <TagInput
                    tags={((attrs.disallowedTools as string) || '').split(',').map((s) => s.trim()).filter(Boolean)}
                    onChange={(tags) => updateAttr('disallowedTools', tags.join(', '))}
                    placeholder="Write, Edit"
                  />
                </div>
              </>
            }
          />
        </div>
      )}
    </CollapsibleSection>
  );
}
